<template>
  <div class="card mb-2" ref="card">
    <div class="card-header bg-primary text-white d-flex">
      <a v-b-toggle="`collapse-${cleanId}-card`" class="collapse-icon c-pointer">
        <font-awesome-icon class="when-opened" icon='caret-down' size="lg" />
        <font-awesome-icon class="when-closed fa-w-10" icon='caret-right' size="lg" />
      </a>
      <span class="font-weight-bold">{{title}}</span>
      <span
        class="ml-auto c-pointer white-space-nowrap"
        @click="startAdding"
        v-if="!$store.getters['User/isCurrentUserViewOnly'] && canAddEntity"
      >
        {{ addLinkLabel }}
      </span>
    </div>
    <b-collapse :id="`collapse-${cleanId}-card`" v-model="showCollapse">
      <div class="card-body">
        <slot name="cardSummary">
        </slot>
        <template v-if="isCurrentlyAdding">
          <slot name="addForm"
                :newEntity="newEntity"
                :options="options"
                :entities="entities"
                :stopAdding="stopAdding"></slot>
        </template>
        <div v-if="!entitiesLoading && (!entities || entities.length === 0)">
          No {{entityTitle || title}} have been added to this {{parentTitle}}
        </div>
        <LoadingSpinner v-if="entitiesLoading"
        :message="`Loading ${entityTitle || title}`"  size = '1x'>
        </LoadingSpinner>
        <template v-if="entities && entities.length >= 0 && !entitiesLoading">
          <transition-group name="card-list">
            <div v-for='(entity, index) in visibleEntities'
                :key='index'>
              <template v-if="currentlyEditingIds && currentlyEditingIds.some(id => id === entity.id)">
                <slot name="editForm"
                  :entity="clonedObject(entity)"
                  :options="options"
                  :stopEditing="stopEditing"
                  >
                </slot>
              </template>
              <div class="card listing-card"
                :class="viewBorderClass(entity)"
                v-if="currentlyEditingIds && !currentlyEditingIds.some(id => id === entity.id)">
                <slot name="cardHeader" :entity="entity">
                </slot>
                <div class="card-body">
                  <div class="row">
                    <div class="col-md-10">
                      <slot name="entityDetails" :entity="entity"></slot>
                    </div>
                    <div class="col-md-2 pl-md-0 d-flex flex-row-reverse
                     flex-md-column justify-content-between justify-content-md-start">
                      <template v-if="!$store.getters['User/isCurrentUserViewOnly']">
                        <div class="d-flex justify-content-end">
                          <b-button variant="danger" size="sm" class="mr-2 mb-2"
                            v-if="!deleteDisabled(entity)"
                            v-b-modal="`deleteConfirmation-${title}-${entity.id}`"
                            :aria-label="deleteTitle">
                            <font-awesome-icon icon="trash-alt" class="text-white" fixed-width />
                          </b-button>
                          <b-button
                            v-if="!editDisabled(entity)"
                            size="sm"
                            @click="editEntity(entity)"
                            class="mb-2 mr-2"
                            aria-label="Edit">
                            <font-awesome-icon icon="pen" class="text-white" fixed-width />
                          </b-button>
                        </div>
                        <div class="d-flex justify-content-end mb-2">
                          <slot name="additionalActions" :entity="entity">
                          </slot>
                        </div>
                      </template>
                    </div>
                  </div>
                </div>
              </div>
              <DeleteModal
                :modalId="`deleteConfirmation-${title}-${entity.id}`"
                :deleteDisplayName="deleteTitle"
                :deletePrompt="deletePrompt"
                @deleteConfirmed="deleteEntity(entity)">
              </DeleteModal>
            </div>
          </transition-group>
          <PaginationControls
           :orderedEntities="orderedEntities"
           :initialPageSize="initialPageSize"
           :pageSize="pageSize"
           :pageSizeOptions="pageSizeOptions"
           :currentPage="currentPage"
           :firstVisibleEntry="firstVisibleEntry"
           :lastVisibleEntry="lastVisibleEntry"
           :rows="rows"
           @update-current-page="updateCurrentPage"
           @update-page-size="updatePageSize">
          </PaginationControls>
        </template>
      </div>
    </b-collapse>
  </div>
</template>
<script>
import CollapseControl from '@/mixins/CollapseControl';
import Pagination from '@/mixins/Pagination';
import DeleteModal from '../DeleteModal.vue';
import LoadingSpinner from '../LoadingSpinner.vue';
import PaginationControls from './PaginationControls.vue';

export default {
  name: 'BaseListCard',
  components: {
    DeleteModal,
    LoadingSpinner,
    PaginationControls,
  },
  mixins: [CollapseControl, Pagination],
  props: {
    // this is a function that should be overridden by the child components.  This default is
    // just by id, so it would order by the id of the entities
    entitySort: {
      type: Function,
      default() {
        return (a, b) => a.id - b.id;
      },
    },
    title: String,
    entityTitle: {
      type: String,
      required: false,
    },
    viewBorderClass: {
      type: Function,
      default: () => '',
    },
    parentId: Number,
    parentTitle: String,
    // this is specifically for select box values and status of the values being loaded
    options: {
      type: Object,
      required: false,
    },
    // the list of entities that are being listed, added to, and edited
    entities: Array,
    // the status of whether the list of entities has been retrieved from the server yet
    entitiesLoading: {
      type: Boolean,
      required: false,
    },
    // a function that should provide a way to get a default entity
    emptyEntity: Function,
    // a function that deletes the entity through the api and removes it from entities
    deleteEntity: Function,

    // a function that returns true if the entity cannot be deleted
    deleteDisabled: {
      type: Function,
      default: () => false,
    },
    // a function that returns true if the entity cannot be edited
    editDisabled: {
      type: Function,
      default: () => false,
    },
    // optional string for the delete modal title
    deleteTitle: {
      type: String,
      default: () => 'Delete',
    },
    // optional string for the text displayed in delete modal
    deletePrompt: {
      type: String,
      default() {
        return 'Are you sure you want to delete?';
      },
    },
    addLinkLabel: {
      type: String,
      default() {
        return '+ Add';
      },
    },
    canAddEntity: {
      type: Boolean,
      default: true,
    },
    initialShowCollapse: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      newEntity: this.emptyEntity(this.parentId),
      isCurrentlyAdding: false,
      currentlyEditingIds: [],
      showCollapse: false,
      cleanId: null,
    };
  },
  mounted() {
    this.showCollapse = this.initialShowCollapse;
    this.cleanId = this.title.replace(/ /g, '');
  },
  methods: {
    startAdding() {
      this.isCurrentlyAdding = true;
      this.showCollapse = true;
      this.$store.dispatch('Common/startEditMode', this.title);
    },
    stopAdding() {
      this.isCurrentlyAdding = false;
      this.newEntity = this.emptyEntity(this.parentId);
      this.$store.dispatch('Common/stopEditMode', this.title);
      this.$refs.card.scrollIntoView();
    },
    editEntity(obj) {
      this.currentlyEditingIds.push(obj.id);
      this.$store.dispatch('Common/startEditMode', this.title);
    },
    stopEditing(id) {
      this.currentlyEditingIds = this.currentlyEditingIds.filter(thisId => thisId !== id);
      this.$store.dispatch('Common/stopEditMode', this.title);
      this.$refs.card.scrollIntoView();
    },
    clonedObject(obj) {
      const newObj = this.emptyEntity(obj.parentId);
      Object.assign(newObj, obj);
      return newObj;
    },
    entityCreated(entity) {
      this.entities.push(entity);
    },
    entityUpdated(entity) {
      this.entities.splice(this.entities.findIndex(e => e.id === entity.id), 1, entity);
    },
  },
};
</script>
